import { loadStripe } from "@stripe/stripe-js";
import { api } from "./api";

// Initialize Stripe with required publishable key (fail fast if missing)
const rawStripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
if (
  !rawStripeKey ||
  typeof rawStripeKey !== "string" ||
  rawStripeKey.trim() === ""
) {
  throw new Error(
    "VITE_STRIPE_PUBLISHABLE_KEY is required but was not provided."
  );
}
const stripePublishableKey: string = rawStripeKey;

const stripePromise = loadStripe(stripePublishableKey);

export interface CreateCheckoutSessionRequest {
  amount: number;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface BillingInfo {
  balance: number;
  totalSpent: number;
  totalDeposited: number;
  lastChargeAt?: string;
  createdAt: string;
  billingMode: string;
  azureSubscriptionId?: string;
  azureTenantId?: string;
  pricing: {
    goodAsHumanCredits: number;
    betterThanHumanCredits: number;
  };
}

export interface BillingTransaction {
  id: string;
  userId: string;
  tenantId: string;
  transactionType: "Deposit" | "Charge" | "Refund" | "Adjustment";
  amount: number;
  balanceAfter: number;
  description: string;
  stripePaymentIntentId?: string;
  stripeSessionId?: string;
  processingJobId?: string;
  createdAt: string;
  metadata?: Record<string, string>;
}

export const stripeService = {
  async createCheckoutSession(
    amount: number
  ): Promise<CreateCheckoutSessionResponse> {
    try {
      const response = await api.createCheckoutSession(amount);

      if (!response.data) {
        throw new Error("Failed to create checkout session");
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async createSubscriptionCheckoutSession(plan: "BasicMonthly" | "ProMonthly"): Promise<CreateCheckoutSessionResponse> {
    const response = await api.post("/billing/stripe/checkout-subscription", { plan });
    if (!response.data?.data) {
      throw new Error("Failed to create subscription checkout session");
    }
    return response.data.data;
  },

  async startSubscription(plan: "BasicMonthly" | "ProMonthly"): Promise<void> {
    const session = await this.createSubscriptionCheckoutSession(plan);
    await this.redirectToCheckout(session.sessionId);
  },

  async redirectToCheckout(sessionId: string): Promise<void> {
    const stripe = await stripePromise;
    if (!stripe) {
      throw new Error("Stripe failed to initialize");
    }

    const { error } = await stripe.redirectToCheckout({
      sessionId,
    });

    if (error) {
      throw new Error(error.message);
    }
  },

  async getBillingInfo(): Promise<BillingInfo> {
    const response = await api.getBillingInfo();
    return response.data;
  },

  async getBalance(): Promise<number> {
    const response = await api.getBillingBalance();
    if (response.data === undefined) {
      throw new Error("Failed to get balance");
    }
    return response.data;
  },

  async getTransactions(
    limit: number = 50,
    skip: number = 0
  ): Promise<BillingTransaction[]> {
    const response = await api.getBillingTransactions(limit, skip);
    if (!response.data) {
      throw new Error("Failed to get transactions");
    }
    return response.data;
  },

  async purchaseCredits(amount: number): Promise<void> {
    const session = await this.createCheckoutSession(amount);
    await this.redirectToCheckout(session.sessionId);
  },

  isAzureMarketplaceUser(billingInfo: BillingInfo): boolean {
    return billingInfo.billingMode === "AzureMarketplace";
  },

  getAzureBillingPortalUrl(billingInfo: BillingInfo): string | null {
    if (!this.isAzureMarketplaceUser(billingInfo)) {
      return null;
    }

    // Azure portal URL for billing overview
    // If we have a specific subscription ID, we could link directly to it, but the general billing overview is more reliable
    return `https://portal.azure.com/#view/Microsoft_Azure_Billing/ModernBillingMenuBlade/~/Overview`;
  },

  async getPricingPlan(): Promise<string> {
    const response = await api.getPricingPlan();
    return response.data!!;
  },

  async updatePricingPlan(pricingPlan: string): Promise<string> {
    const response = await api.updatePricingPlan(pricingPlan);
    return response.data!!;
  },
};
